import { Injectable } from '@angular/core';
import {
  CanActivate,
  Router,
  UrlTree,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '../api/services/auth.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree | Observable<boolean | UrlTree> {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        const url = new URL(window.location.href);
        let params = url.searchParams;

        if (url.hash && url.hash.includes('?')) {
          const hashQuery = url.hash.split('?')[1];
          params = new URLSearchParams(hashQuery);
        } else if (
          url.hash &&
          !url.hash.includes('/') &&
          url.hash.includes('=')
        ) {
          const hashPath = url.hash.startsWith('#')
            ? url.hash.substring(1)
            : url.hash;
          params = new URLSearchParams(hashPath);
        }

        if (state.url.includes('/eciso/executive-dashboard')) {
          if (
            localStorage.length === 0 ||
            !localStorage.getItem('accessToken')
          ) {
            params.forEach((value, key) => {
              localStorage.setItem(key, value);
            });
          }
        }

        const accessToken = params.get('accessToken') || params.get('token');
        if (accessToken) {
          localStorage.setItem('accessToken', accessToken);

          const email = params.get('email');
          if (email) localStorage.setItem('email', email);

          const pmSetup = params.get('pmSetup');
          if (pmSetup !== null && pmSetup !== undefined) {
            localStorage.setItem('pmSetup', pmSetup);
          }

          const refreshToken = params.get('refreshToken');
          if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

          const role = params.get('role');
          if (role) {
            localStorage.setItem('role', role);
          }

          // Clear credentials from the address bar by navigating to the clean URL
          const cleanUrl = state.url.split('?')[0].split('#')[0];
          this.router.navigateByUrl(cleanUrl, { replaceUrl: true });
          return false;
        }
      } catch (e) {}
    }

    const token = localStorage.getItem('accessToken');
    if (token) {
      return true;
    }

    // Try to retrieve tokens from environment api_endpoint if it's on a different origin/port
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        const currentOrigin = window.location.origin;
        const apiOrigin = new URL(environment.api_endpoint).origin;

        if (currentOrigin !== apiOrigin) {
          const subject = new Subject<boolean | UrlTree>();

          // Create hidden iframe
          const iframe = document.createElement('iframe');
          iframe.src = `${apiOrigin}/eciso/executive-dashboard`;
          iframe.style.display = 'none';

          const messageHandler = (event: MessageEvent) => {
            if (
              event.origin === apiOrigin &&
              event.data &&
              event.data.type === 'sso-tokens'
            ) {
              const data = event.data;

              localStorage.setItem('accessToken', data.accessToken);
              if (data.email) localStorage.setItem('email', data.email);
              if (data.pmSetup !== null && data.pmSetup !== undefined) {
                localStorage.setItem('pmSetup', String(data.pmSetup));
              }
              if (data.refreshToken)
                localStorage.setItem('refreshToken', data.refreshToken);
              if (data.role) {
                localStorage.setItem(
                  'role',
                  typeof data.role === 'string'
                    ? data.role
                    : JSON.stringify(data.role),
                );
              }

              // Clean up
              cleanup();
              subject.next(true);
              subject.complete();
            }
          };

          let cleanup = () => {
            window.removeEventListener('message', messageHandler);
            if (iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          };

          // Timeout after 2.5 seconds if no tokens are received
          const timeoutId = setTimeout(() => {
            cleanup();
            subject.next(this.router.createUrlTree(['/login']));
            subject.complete();
          }, 2500);

          const originalCleanup = cleanup;
          cleanup = () => {
            clearTimeout(timeoutId);
            originalCleanup();
          };

          window.addEventListener('message', messageHandler, false);
          document.body.appendChild(iframe);

          return subject;
        }
      } catch (e) {
        console.error('[AuthGuard] Error during iframe token retrieval:', e);
      }
    }

    return this.router.createUrlTree(['/login']);
  }
}
